import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth as useAuthContext, type AuthUser, type AuthContextValue } from '@/context/AuthContext';
import { hasToken } from '@/lib/api-client';

export type { AuthUser };

export interface UseAuthReturn extends AuthContextValue {
  /** Login and navigate to dashboard on success */
  loginAndRedirect: (email: string, password: string) => Promise<void>;
  /** Register and navigate to dashboard on success */
  registerAndRedirect: (name: string, email: string, password: string, firmName: string) => Promise<void>;
  /** Logout and navigate to login page */
  logoutAndRedirect: () => void;
  /** Whether a token exists in localStorage (sync check, no API call) */
  hasStoredToken: boolean;
}

/**
 * Auth hook that wraps AuthContext with navigation helpers.
 * Provides login/register/logout with automatic redirects.
 */
export function useAuthActions(): UseAuthReturn {
  const auth = useAuthContext();
  const navigate = useNavigate();

  const loginAndRedirect = useCallback(
    async (email: string, password: string) => {
      await auth.login(email, password);
      navigate('/', { replace: true });
    },
    [auth, navigate],
  );

  const registerAndRedirect = useCallback(
    async (name: string, email: string, password: string, firmName: string) => {
      await auth.register(name, email, password, firmName);
      navigate('/', { replace: true });
    },
    [auth, navigate],
  );

  const logoutAndRedirect = useCallback(() => {
    auth.logout();
    navigate('/login', { replace: true });
  }, [auth, navigate]);

  return {
    ...auth,
    loginAndRedirect,
    registerAndRedirect,
    logoutAndRedirect,
    hasStoredToken: hasToken(),
  };
}

/**
 * Convenience re-export of the base context hook for components
 * that don't need navigation (e.g., outside Router).
 */
export { useAuth } from '@/context/AuthContext';
